/**
 * Rival Memory — the shared toolkit that turns campaign rivals into persistent
 * characters. Pure and RNG-free (like combatActionRules): consumed by the
 * backend (encounter recording, rematch squad scaling) AND the frontend
 * (dialogue variant selection, rematch squad preview), so both always agree.
 *
 * Design (docs/superpowers/specs/2026-07-06-campaign-rival-memory-design.md):
 * worlds stay freely orderable — memory is strictly PER-RIVAL, keyed by a
 * `rivalId` derived from the authored rivalName. The same name across nodes is
 * deliberately the same character (that's what makes recurrence possible).
 */

import { getStageForLevel } from '../petHelper.js';
import type { CampaignNode, CampaignRivalPet } from './builders.js';

// ============================================================================
// Rival identity
// ============================================================================

/**
 * Stable rival identity derived from the authored display name, e.g.
 * "The Hollow Elder" → "the-hollow-elder". Never change the derivation after
 * release — encounter rows are keyed by it.
 */
export function rivalIdForName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ============================================================================
// Encounter memory
// ============================================================================

/**
 * A player's battle history with ONE rival character (from the
 * campaign_encounters table). `lastResult` is from the player's perspective.
 */
export interface RivalEncounterSummary {
  rivalId: string;
  wins: number;
  losses: number;
  lastResult: 'win' | 'loss' | null;
}

/** True when the player has fought this rival before (any outcome). */
export function hasMetRival(encounter: RivalEncounterSummary | undefined): boolean {
  return !!encounter && encounter.wins + encounter.losses > 0;
}

// ============================================================================
// Dialogue variant selection
// ============================================================================

/**
 * A node's dialogue lines. The three base lines are REQUIRED (authored for
 * every node); the rematch/repeat variants are optional — a missing variant
 * falls back to its base line, so worlds can be upgraded incrementally.
 */
export interface NodeDialogue {
  /** Rival's line before the battle (first meeting). */
  intro: string;
  /** Rival's line when the player wins (first victory over them). */
  playerWin: string;
  /** Rival's line when the player loses (first defeat). */
  playerLose: string;
  /** Rematch intro when the rival BEAT the player last time. */
  introRematchAfterLoss?: string;
  /** Rematch intro when the player beat the rival last time. */
  introRematchAfterWin?: string;
  /** Win line when the player has beaten this rival before. */
  playerWinRepeat?: string;
  /** Lose line when this rival has beaten the player before. */
  playerLoseRepeat?: string;
  /**
   * Optional line the PLAYER (the Keeper) speaks back before the battle, shown
   * with the player's own profile avatar rather than Shika's narrator portrait.
   * Purely presentational: when absent, the player simply says nothing and no
   * player beat is rendered, so worlds can add Keeper voice incrementally.
   */
  playerIntro?: string;
}

/**
 * Pick the rival's pre-battle line from the player's history with them.
 * `encounter` is the CURRENT (pre-battle) summary.
 */
export function selectIntroLine(
  dialogue: NodeDialogue,
  encounter: RivalEncounterSummary | undefined
): string {
  if (!hasMetRival(encounter)) return dialogue.intro;
  if (encounter!.lastResult === 'loss') {
    return dialogue.introRematchAfterLoss ?? dialogue.intro;
  }
  return dialogue.introRematchAfterWin ?? dialogue.intro;
}

/**
 * Pick the rival's post-battle line. `encounter` must be the PRE-battle
 * summary (the just-finished battle is NOT counted in it): a repeat win means
 * the player had already beaten this rival before this battle.
 */
export function selectOutcomeLine(
  dialogue: NodeDialogue,
  playerWon: boolean,
  encounter: RivalEncounterSummary | undefined
): string {
  if (playerWon) {
    const repeat = (encounter?.wins ?? 0) > 0;
    return repeat ? (dialogue.playerWinRepeat ?? dialogue.playerWin) : dialogue.playerWin;
  }
  const repeat = (encounter?.losses ?? 0) > 0;
  return repeat ? (dialogue.playerLoseRepeat ?? dialogue.playerLose) : dialogue.playerLose;
}

// ============================================================================
// Rematch squad scaling — "the rival trained too"
// ============================================================================

/**
 * On a REMATCH (node already cleared), the rival's squad grows toward the
 * player's own strength: each rival fights at
 * max(authored level, min(node cap, effectivePlayerLevel)), with the stage
 * re-derived — so a rival's lead pet can visibly EVOLVE between encounters.
 * Never scales below the authored level, never above the world cap. Pure:
 * returns copies, never mutates the shared config.
 *
 * Used by the backend on battle create AND the frontend squad preview so the
 * preview always matches the battle.
 */
export function scaleRivalSquadForRematch(
  node: CampaignNode,
  effectivePlayerLevel: number,
  /** Override the level ceiling (default: the node's playerLevelCap). The Rival of the Day
   *  passes DAILY_RIVAL_LEVEL_CAP so it scales to the player regardless of world. */
  levelCapOverride?: number
): CampaignRivalPet[] {
  const target = Math.min(levelCapOverride ?? node.playerLevelCap, Math.floor(effectivePlayerLevel));
  return node.squad.map((pet) => {
    const level = Math.max(pet.level, target);
    return { ...pet, level, stage: getStageForLevel(pet.speciesId, level) };
  });
}
